import { Injectable } from '@angular/core';
import * as core from 'DiscoveryEngine.TS/Core';
//import * as info from 'DiscoveryEngine.TS/InfoSets';
import * as topo from 'DiscoveryEngine.TS/Topology';
import { TopoMap, DrawSettings, PenPattern, Color, DashStyle } from '../dtos/topology/TopoMap';
import { List, Dictionary, Tuple } from 'DiscoveryEngine.TS/Utils';
//import { DeviceCategories } from 'DiscoveryEngine.TS/Core';
import { DeviceNode, DeviceCategories } from '../dtos/topology/DeviceNode';
import { HubNode } from '../dtos/topology/HubNode';
import { DeviceLink, DeviceLinkMember } from '../dtos/topology/DeviceLink';
import { DeviceLinkEdge } from '../dtos/topology/DeviceLinkEdge';
import { DeviceConnection } from '../dtos/topology/DeviceConnection';
import { Point } from '../dtos/topology/Point';
import { LayoutSettings, HierarchicalLayoutSettings } from '../dtos/topology/LayoutSettings';
import { DeviceGroupNode } from '../dtos/topology/DeviceGroupNode';


@Injectable()
export class MapConversionService {

	constructor() {

	}

	public convertMap(map: topo.TopoMap, mapDrawSettings: topo.DrawSettings): TopoMap {
		let deviceNodes = new List<DeviceNode>();
		let deviceGroupNodes = new List<DeviceGroupNode>();
		let hubNodes = new List<HubNode>();
		let deviceLinks = new List<DeviceLink>();

		// Ids for correlating device/hub nodes to device connections
		// (when a node is dragged, its device connections should follow it)
		let nodeIds = new Dictionary<any, number>();
		let nodeIdCounter = 0;

		map.VisibleNodes.ForEach((node) => {
			if (node instanceof topo.DeviceNode) {
				let nodeId = ++nodeIdCounter;
				nodeIds.Add(node, nodeId);

				let dto = this.toDeviceNodeDto(node as topo.DeviceNode, mapDrawSettings /*, prtgGroupId*/, nodeId);
				deviceNodes.Add(dto);
			}
			if (node instanceof topo.DeviceGroupNode) {
				let nodeId = ++nodeIdCounter;
				nodeIds.Add(node, nodeId);

				let dto = this.toDeviceGroupNodeDto(node as topo.DeviceGroupNode, mapDrawSettings /*, prtgGroupId*/, nodeId);
				deviceGroupNodes.Add(dto);
			}
			if (node instanceof topo.MultiNode) {
				let nodeId = ++nodeIdCounter;
				nodeIds.Add(node, nodeId);

				let dto = this.toHubNodeDto(node as topo.MultiNode, nodeId);
				hubNodes.Add(dto);
			}
		}, this);

		let linkMemberMap = new Dictionary<core.Link, List<Tuple<topo.TopoLinkMember, topo.TopoEdge>>>();
		let assocMemberList = new List<Tuple<topo.TopoLinkMember, topo.TopoEdge>>();
		map.TopoEdges.ForEach(topoEdge => {
			topoEdge.Links.ForEach(topoLinkMember => {
				let link = this.getLink(topoLinkMember);
				if (link != null) {
					if (!linkMemberMap.ContainsKey(link)) {
						linkMemberMap.Add(link, new List<Tuple<topo.TopoLinkMember, topo.TopoEdge>>());
					}

					linkMemberMap.Item(link).Add(new Tuple<topo.TopoLinkMember, topo.TopoEdge>(topoLinkMember, topoEdge));
				}
				else if (topoLinkMember.LocalConnection instanceof topo.AssociatedConnection &&
					topoLinkMember.RemoteConnection instanceof topo.AssociatedConnection) {

					assocMemberList.Add(new Tuple<topo.TopoLinkMember, topo.TopoEdge>(topoLinkMember, topoEdge));
				}
			}, this);
		}, this);

		linkMemberMap.ForEach(kvp => {
			let link = kvp.Key;
			let tuples = kvp.Value;

			let linkEdges = new List<DeviceLinkEdge>();
			tuples.ForEach(tuple => {
				let topoLinkMember = tuple.Item1;
				let topoEdge = tuple.Item2;
				linkEdges.Add(this.toDeviceLinkEdgeDto(topoLinkMember, topoEdge, map, mapDrawSettings, nodeIds));
			}, this);

			let linkDto: DeviceLink = <DeviceLink>{};
			linkDto.linkEdges = linkEdges.ToArray();
			this.populateLinkMemberDetails(link, linkDto);
			deviceLinks.Add(linkDto);
		}, this);

		assocMemberList.ForEach(tuple => {
			let topoLinkMember = tuple.Item1;
			let topoEdge = tuple.Item2;

			let linkEdges = new List<DeviceLinkEdge>();
			linkEdges.Add(this.toDeviceLinkEdgeDto(topoLinkMember, topoEdge, map, mapDrawSettings, nodeIds));
			
			let linkDto: DeviceLink = <DeviceLink>{};
			linkDto.linkEdges = linkEdges.ToArray();
			linkDto.linkMembers = [];
			linkDto.linkType = "Associated";
			linkDto.allWirelessOrVm = false;
			linkDto.noWireless = true;
			linkDto.noVm = true;
			deviceLinks.Add(linkDto);
		}, this);	

		let layoutSettingsDto = this.toLayoutSettingsDto(map.LayoutSettings);

		let drawSettingsDto = this.toDrawSettingsDto(mapDrawSettings);

		return <TopoMap>{
			layoutSettings: layoutSettingsDto,
			drawSettings: drawSettingsDto,
			height: map.Height,
			width: map.Width,
			top: map.Top,
			bottom: map.Bottom,
			left: map.Left,
			right: map.Right,
			centerX: map.CenterX,
			centerY: map.CenterY,
			deviceNodes: deviceNodes.ToArray(),
			deviceGroupNodes: deviceGroupNodes.ToArray(),
			deviceLinks: deviceLinks.ToArray(),
			hubNodes: hubNodes.ToArray()
		};
	}

	private toHubNodeDto(hubNode: topo.MultiNode, nodeId: number): HubNode {
		return <HubNode> {
			nodeId: nodeId,
			label: hubNode.Description,
			width: hubNode.Width,
			height: hubNode.Height,
			centerX: hubNode.CenterX,
			bottom: hubNode.Bottom,
			x: hubNode.X,
			y: hubNode.Y,
			type: hubNode.Type
		}
	}

	private toDeviceGroupNodeDto(deviceGroupNode: topo.DeviceGroupNode, mapDrawSettings: topo.DrawSettings /*, int prtgGroupId*/,
		nodeId: number): DeviceGroupNode {

		let id = -1;

		let deviceGroupGuid = "";
		let displayName = "";
		let deviceCount = 0;
		if (deviceGroupNode.HasDeviceGroup && deviceGroupNode.DeviceGroup.HasGUID) {
			deviceGroupGuid = deviceGroupNode.DeviceGroup.VerifyGuid();
			displayName = deviceGroupNode.DeviceGroup.Name;
			deviceCount = deviceGroupNode.DeviceGroup.Members.Count;
		}

		return <DeviceGroupNode>{
			id: id,
			nodeId: nodeId,
			deviceGroupGuid: deviceGroupGuid,
			displayName: displayName,
			deviceCount: deviceCount,
			width: deviceGroupNode.Width,
			height: deviceGroupNode.Height,
			centerX: deviceGroupNode.CenterY,
			bottom: deviceGroupNode.Bottom,
			x: deviceGroupNode.X,
			y: deviceGroupNode.Y
		}
	}

	private toDeviceNodeDto(deviceNode: topo.DeviceNode, mapDrawSettings: topo.DrawSettings /*, int prtgGroupId*/,
		nodeId: number): DeviceNode {

		let id = -1;

		//if (deviceNode.Device.HasPrtgDeviceInfo) {
		//	let prtgDeviceEntry = deviceNode.Device.PrtgDeviceInfo.FindByGroupID(prtgGroupId);
		//	id = prtgDeviceEntry.PrtgID;
		//}

		let deviceGuid = "";
		if (deviceNode.HasDevice && deviceNode.Device.HasGUID)
			deviceGuid = deviceNode.Device.VerifyGUID();

		return <DeviceNode>{
			id: id,
			nodeId: nodeId,
			deviceGuid: deviceGuid,
			categories: this.getCategories(deviceNode),
			displayName: this.getDisplayName(deviceNode, mapDrawSettings),
			status: deviceNode.DeviceState,
			vendor: this.getVendor(deviceNode),
			width: deviceNode.Width,
			height: deviceNode.Height,
			centerX: deviceNode.CenterX,
			centerY: deviceNode.CenterY,
			bottom: deviceNode.Bottom,
			x: deviceNode.X,
			y: deviceNode.Y
		}
	}

	private getCategories(deviceNode: topo.DeviceNode): DeviceCategories {
		let result = <DeviceCategories>{
			entries: []
		};

		if (deviceNode.HasDevice) {
			deviceNode.Device.Categories.ForEach((entry: core.DeviceCategoriesEntry) => {
				result.entries.push({
					categoryName: entry.Category.Name,
					categoryType: entry.Category.Type
				});
			}, this);
		}

		return result;
	}

	private getDisplayName(deviceNode: topo.DeviceNode, mapDrawSettings: topo.DrawSettings): string {

		let result: string = deviceNode.Label;

		if (mapDrawSettings.ShortDeviceNames) {
			if (mapDrawSettings.DeviceTrimLeft) {
				for (let i = 0; i < mapDrawSettings.DeviceTrimLeftCount; ++i) {
					let index = result.indexOf(mapDrawSettings.DeviceTrimLeftChar);
					if (index >= 0) {
						result = result.substr(index + 1);
					}
				}
			}

			if (mapDrawSettings.DeviceTrimRight) {
				for (let i = 0; i < mapDrawSettings.DeviceTrimRightCount; ++i) {
					let index = result.lastIndexOf(mapDrawSettings.DeviceTrimRightChar);
					if (index >= 0) {
						result = result.substr(0, index);
					}
				}
			}
		}

		return result;
	}

	private getVendor(deviceNode: topo.DeviceNode): string {
		let vendor = "";

		let device = deviceNode.Device;

		if (device && device.HasProductInfo && device.ProductInfo.HasVendor) {
			vendor = device.ProductInfo.Vendor;
		}

		if (!vendor && deviceNode.HasDeviceGroupMember) {
			vendor = deviceNode.DeviceGroupMember.Vendor;
		}

		return vendor;
	}

	private populateLinkMemberDetails(link: core.Link, deviceLinkDto: DeviceLink): void {
		let linkMemberDtos = new List<DeviceLinkMember>();

		let noWireless: boolean = true;
		let noVm: boolean = true;
		let allWirelessOrVm: boolean = true;
		let deviceName: string;
		let ifIndex: number;
		let ifName: string;

		if (link.LinkType == core.LinkType.WirelessEndNode) {
			noWireless = false;

			link.LinkMembers.ForEach((linkMember: core.LinkMember) => {
				deviceName = "";
				ifIndex = -1;
				ifName = "";
				let radio = "";
				let ssid = "";

				if (linkMember.HasDevice)
					deviceName = linkMember.Device.DisplayName;

				if (linkMember.HasIfEntry) {
					ifIndex = linkMember.IfEntry.IfIndex;
					ifName = linkMember.IfEntry.Name;
					radio = linkMember.IfEntry.Description;
				}

				if (linkMember.HasSSID) {
					ssid = linkMember.SSID;
				}

				linkMemberDtos.Add(<DeviceLinkMember>{
					deviceName: deviceName,
					ifIndex: ifIndex,
					ifName: ifName,
					radio: radio,
					ssid: ssid,
					linkType: core.LinkType[link.LinkType],
				});
			}, this);
		}
		else if (link.LinkType == core.LinkType.VirtualMachine) {
			noVm = false;

			link.LinkMembers.ForEach((linkMember: core.LinkMember) => {
				deviceName = "";
				ifIndex = -1;
				ifName = "";
				let virtualPort = "";
				let virtualPortGroup = "";

				if (linkMember.HasDevice)
					deviceName = linkMember.Device.DisplayName;

				if (linkMember.HasIfEntry) {
					ifIndex = linkMember.IfEntry.IfIndex;
					ifName = linkMember.IfEntry.Name;
				}

				if (linkMember.HasVirtualPortGroup) {
					virtualPortGroup = linkMember.VirtualPortGroup;
				}

				if (linkMember.HasVirtualPort) {
					virtualPort = linkMember.VirtualPort;
				}

				linkMemberDtos.Add(<DeviceLinkMember>{
					deviceName: deviceName,
					ifIndex: ifIndex,
					ifName: ifName,
					virtualPort: virtualPort,
					virtualPortGroup: virtualPortGroup,
					linkType: core.LinkType[link.LinkType],
				});
			}, this);
		}
		else {
			allWirelessOrVm = false;

			let connectedDevice: string;
			let connectedIfIndex: number;
			let connectedIfName: string;

			if (link.LinkMembers.Count == 2) {
				let source: core.LinkMember = link.LinkMembers.ToArray()[0];
				let target: core.LinkMember = link.LinkMembers.ToArray()[1];

				deviceName = "";
				ifIndex = -1;
				ifName = "";
				connectedDevice = "";
				connectedIfIndex = -1;
				connectedIfName = "";

				if (source.HasDevice)
					deviceName = source.Device.DisplayName;

				if (source.HasIfEntry) {
					ifIndex = source.IfEntry.IfIndex;
					ifName = source.IfEntry.Name;
				}
				else if (source.HasIfKey) {
					ifName = source.IfKey.ToString();
				}

				if (target.HasDevice)
					connectedDevice = target.Device.DisplayName;

				if (target.HasIfEntry) {
					connectedIfIndex = target.IfEntry.IfIndex;
					connectedIfName = target.IfEntry.Name;
				}
				else if (target.HasIfKey) {
					connectedIfName = target.IfKey.ToString();
				}

				linkMemberDtos.Add(<DeviceLinkMember>{
					deviceName: deviceName,
					ifIndex: ifIndex,
					ifName: ifName,
					connectedDevice: connectedDevice,
					connectedIfIndex: connectedIfIndex,
					connectedIfName: connectedIfName,
					linkType: core.LinkType[link.LinkType],
				});
			}
			else if (link.LinkMembers.Count > 2) {
				connectedDevice = "Unmanaged";
				connectedIfIndex = -1;
				connectedIfName = "";

				link.LinkMembers.ForEach((linkMember: core.LinkMember) => {
					deviceName = "";
					ifIndex = -1;
					ifName = "";

					if (linkMember.HasDevice) {
						deviceName = linkMember.Device.DisplayName;
					}

					if (linkMember.HasIfEntry) {
						ifIndex = linkMember.IfEntry.IfIndex;
						ifName = linkMember.IfEntry.Name;
					}

					linkMemberDtos.Add(<DeviceLinkMember>{
						deviceName: deviceName,
						ifIndex: ifIndex,
						ifName: ifName,
						connectedDevice: connectedDevice,
						connectedIfIndex: connectedIfIndex,
						connectedIfName: connectedIfName,
						linkType: core.LinkType[link.LinkType],
					});
				}, this);
			}
		}

		deviceLinkDto.linkType = core.LinkType[link.LinkType];
		deviceLinkDto.allWirelessOrVm = allWirelessOrVm;
		deviceLinkDto.noWireless = noWireless;
		deviceLinkDto.noVm = noVm;
		deviceLinkDto.linkMembers = linkMemberDtos.ToArray();
	}

	private getLink(member: topo.TopoLinkMember): core.Link {
		if (member.LocalConnection instanceof topo.LinkConnection) {
			let linkConn = member.LocalConnection as topo.LinkConnection;
			if (linkConn != null && linkConn.Link != null) {
				return linkConn.Link;
			}
		}

		if (member.RemoteConnection instanceof topo.LinkConnection) {
			let linkConn = member.RemoteConnection as topo.LinkConnection;
			if (linkConn != null && linkConn.Link != null) {
				return linkConn.Link;
			}
		}

		return null;
	}

	private topoConnectionLabel(connection: topo.TopoConnection, shortNames: boolean): List<string> {
		let result = new List<string>();

		if (connection instanceof topo.DeviceConnection) {
			let deviceConnection = connection as topo.DeviceConnection;
			if (deviceConnection != null) {
				if (deviceConnection.LinkMembers.Count > 0) {
					deviceConnection.LinkMembers.ForEach(linkMember => {
						result.Add(this.deviceInterfaceLabel(linkMember, shortNames));
					}, this);
				}

				return result;
			}
		}

		if (connection instanceof topo.MultiNodeConnection) {
			let hubConnection = connection as topo.MultiNodeConnection;
			if (hubConnection != null) {
				result.Add(hubConnection.Description);
				return result;
			}
		}

		if (connection instanceof topo.AssociatedConnection) {
			let assocConnection = connection as topo.AssociatedConnection;
			if (assocConnection != null) {
				result.Add(assocConnection.DisplayName);
			}
		}

		return result;
	}

	private deviceInterfaceLabel(linkMember: core.LinkMember, shortNames: boolean): string {
		let label: string;

		if (linkMember.IfEntry != null) {
			if (shortNames)
				label = linkMember.IfEntry.ShortDisplayName;
			else
				label = linkMember.IfEntry.DisplayName;
		}
		else if (linkMember.IfKey != null) {
			let tempLabel = "";

			if (linkMember.HasDescription)
				tempLabel = `{linkMember.IfKey.IfName} - {linkMember.Description}`;
			else
				tempLabel = linkMember.IfKey.IfName;

			label = tempLabel;
		}
		else if (linkMember.HasDescription) {
			label = linkMember.Description;
		}
		else {
			label = " ";
		}

		return label;
	}

	private toDeviceLinkEdgeDto(topoLinkMember: topo.TopoLinkMember, topoEdge: topo.TopoEdge,
		map: topo.TopoMap, mapDrawSettings: topo.DrawSettings,
		nodeIds: Dictionary<any, number>): DeviceLinkEdge {

		let remoteInterfaceLabels = new List<string>();
		let localInterfaceLabels = new List<string>();

		let localPoint = <Point>{
			x: topoEdge.LocalNode.CenterX,
			y: topoEdge.LocalNode.CenterY
		};

		let remotePoint = <Point>{
			x: topoEdge.RemoteNode.CenterX,
			y: topoEdge.RemoteNode.CenterY
		};

		let nodeMidPoint: Point = this.midPoint(localPoint, remotePoint);

		let remoteConnMidPoint: Point = this.anglePoint(remotePoint, nodeMidPoint, map.LayoutSettings);
		let localConnMidPoint: Point = this.anglePoint(localPoint, nodeMidPoint, map.LayoutSettings);

		let shortNames: boolean = mapDrawSettings.ShortIfNames;
		remoteInterfaceLabels.AddRange(this.topoConnectionLabel(topoLinkMember.RemoteConnection, shortNames));
		localInterfaceLabels.AddRange(this.topoConnectionLabel(topoLinkMember.LocalConnection, shortNames));

		let remoteNodeId = nodeIds.Item(topoEdge.RemoteNode);
		let localNodeId = nodeIds.Item(topoEdge.LocalNode);

		let deviceLinkDto = <DeviceLinkEdge>{
			remoteConnection: <DeviceConnection>{
				nodeId: remoteNodeId,
				start: remotePoint,
				end: nodeMidPoint,
				mid: remoteConnMidPoint,
				interfaceLabels: remoteInterfaceLabels.ToArray()
			},
			localConnection: <DeviceConnection>{
				nodeId: localNodeId,
				start: localPoint,
				end: nodeMidPoint,
				mid: localConnMidPoint,
				interfaceLabels: localInterfaceLabels.ToArray()
			}
		};

		return deviceLinkDto;
	}

	public midPoint(a: Point, b: Point): Point {
		return <Point>{
			x: ((a.x + b.x) / 2),
			y: ((a.y + b.y) / 2)
		};
	}

	public anglePoint(nodePoint: Point, edgePoint: Point, layoutSettings: topo.LayoutSettings): Point {
		if (layoutSettings.LayoutType != topo.LayoutType.Hierarchical ||
			layoutSettings.HierarchicalSettings.UseStraightLinks) {

			return this.midPoint(nodePoint, edgePoint);
		}
		else {
			switch (layoutSettings.HierarchicalSettings.LayoutDirection) {
				case topo.LayoutDirection.Right:
				case topo.LayoutDirection.Left:
					return <Point>{ x: edgePoint.x, y: nodePoint.y };
				case topo.LayoutDirection.Up:
				case topo.LayoutDirection.Down:
					return <Point>{ x: nodePoint.x, y: edgePoint.y };
				default:
					console.assert(false);
					return <Point>{ x: 0, y: 0 };
			}
		}
	}

	public toLayoutSettingsDto(settings: topo.LayoutSettings): LayoutSettings {

		let hierarchicalSettings: HierarchicalLayoutSettings = null;
		if (settings.LayoutType == topo.LayoutType.Hierarchical) {
			hierarchicalSettings = this.toHierarchicalLayoutSettingsDto(settings.HierarchicalSettings);
		}

		return <LayoutSettings>{
			layoutType: settings.LayoutType,
			hierarchicalSettings: hierarchicalSettings,
		};
	}

	private toHierarchicalLayoutSettingsDto(settings: topo.HierarchicalLayoutSettings): HierarchicalLayoutSettings {
		return <HierarchicalLayoutSettings>{
			levelSpacing: settings.LevelSpacing,
			useStraightLinks: settings.UseStraightLinks,
			nodeSpacing: settings.NodeSpacing,
			layoutDirection: settings.LayoutDirection,
			rootAlignment: settings.RootAlignment,
		};
	}

	private toDrawSettingsDto(settings: topo.DrawSettings): DrawSettings {
		return <DrawSettings>{
			shortDeviceNames: settings.ShortDeviceNames,
			shortIfNames: settings.ShortIfNames,
			deviceTrimLeft: settings.DeviceTrimLeft,
			deviceTrimRight: settings.DeviceTrimRight,
			deviceTrimLeftChar: settings.DeviceTrimLeftChar,
			deviceTrimRightChar: settings.DeviceTrimRightChar,
			deviceTrimLeftCount: settings.DeviceTrimLeftCount,
			deviceTrimRightCount: settings.DeviceTrimRightCount,

			standardPen: this.toPenPatternDto(settings.StandardPen),
			lagPen: this.toPenPatternDto(settings.LagPen),
			manualPen: this.toPenPatternDto(settings.ManualPen),
			associatedPen: this.toPenPatternDto(settings.AssociatedPen),
			multiPen: this.toPenPatternDto(settings.MultiPen),
		};
	}

	private toPenPatternDto(penPattern: topo.PenPattern): PenPattern {
		return <PenPattern>{
			color: this.toColorDto(penPattern.Color),
			width: penPattern.Width,
			dashStyle: this.toDashStyleDto(penPattern.DashStyle),
		};
	}

	private toColorDto(color: topo.Color): Color {
		return <Color>{
			red: color.R,
			green: color.G,
			blue: color.B,
		};
	}

	private toDashStyleDto(dashStyle: topo.DashStyle): DashStyle {
		let valueStr: string = topo.DashStyle[dashStyle];
		return DashStyle[valueStr];
	}
	
}

